import { logger } from '@adornis/base/logging.js';
import * as crypto from 'node:crypto';
import { lastValueFrom } from 'rxjs';
import { BoundingBox } from '../../../BoundingBox.js';
import { OCRProgress } from '../../../ocr-progress.js';
import { Point } from '../../../Point.js';
import { cropAndSaveRegion } from '../../../utils/crop-image.js';
import {
  calculateBaseExtractionConfidence,
  calculateTaskExtractionConfidence,
} from '../calculate-task-extraction-confidence.js';
import { ocrExtract } from '../ocr-agent.js';
import { OcrExtractionType } from './ocrExtractionTypes.js';
import { TaskType, taskTypeBehaviours } from '../../task-types/task-main.js';
import {
  type BoundingBoxContentType,
  LEGACY_CONTENT_TYPE,
  normalizeContentType,
} from '../../../../shared/bounding-box-content.js';
interface OCRResult {
  answer: string;
  confidence: number;
  isHandwritten?: boolean;
  contentType: BoundingBoxContentType;
}

interface OCRRegionInput {
  bbox: { x1: number; y1: number; x2: number; y2: number };
  pageNumber: number;
  confidence?: number;
  boundingBoxId?: string;
  taskType?: string;
}

function stripCodeFences(value: string): string {
  return value
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
}

function normalizeOCRResult(raw: unknown): OCRResult {
  if (raw && typeof raw === 'object') {
    // Trust the authoritative content type produced by the OCR parser; a missing/unknown type
    // normalizes to "both" (the safe fallback), never to "image".
    const directType = normalizeContentType((raw as any).type);
    const directAnswer =
      directType === 'image' ? '' : typeof (raw as any).answer === 'string' ? (raw as any).answer : '';
    const directConfidenceRaw = Number((raw as any).confidence ?? 0);
    const directConfidence = Number.isFinite(directConfidenceRaw) ? Math.max(0, Math.min(1, directConfidenceRaw)) : 0;
    const directIsHandwritten =
      typeof (raw as any).isHandwritten === 'boolean' ? (raw as any).isHandwritten : undefined;

    if (directAnswer || 'confidence' in (raw as any) || 'type' in (raw as any)) {
      return {
        answer: directAnswer,
        confidence: directConfidence,
        isHandwritten: directIsHandwritten,
        contentType: directType,
      };
    }
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return { answer: '', confidence: 0, contentType: LEGACY_CONTENT_TYPE };

    try {
      const parsed = JSON.parse(stripCodeFences(trimmed));
      // Trust the authoritative content type; missing/unknown normalizes to "both", never "image".
      const type = normalizeContentType(parsed?.type);
      const answer = type === 'image' ? '' : typeof parsed?.answer === 'string' ? parsed.answer : '';
      const confidenceRaw = Number(parsed?.confidence ?? 0);
      const confidence = Number.isFinite(confidenceRaw) ? Math.max(0, Math.min(1, confidenceRaw)) : 0;
      const isHandwritten = typeof parsed?.isHandwritten === 'boolean' ? parsed.isHandwritten : undefined;
      if (answer || 'confidence' in (parsed ?? {}) || 'type' in (parsed ?? {})) {
        return { answer, confidence, isHandwritten, contentType: type };
      }
    } catch {
      return { answer: raw, confidence: 0, contentType: LEGACY_CONTENT_TYPE };
    }

    return { answer: raw, confidence: 0, contentType: LEGACY_CONTENT_TYPE };
  }

  return { answer: '', confidence: 0, contentType: LEGACY_CONTENT_TYPE };
}

/**
 * Run OCR on an image using LLM
 * Note: Cropping should be done separately using cropAndSaveRegion before calling this
 * @param imageId The ImageFile ID to extract text from
 * @returns OCR result with answer and confidence
 */
export async function runOCROnImage(
  imageId: string,
  extractionType: OcrExtractionType,
  taskType: string,
  onCost?: (cost: number) => void,
): Promise<OCRResult> {
  try {
    const ocrResolver = ocrExtract(imageId, extractionType, taskType) as unknown as (fields?: unknown) => any;
    const finalProgress: any = await lastValueFrom(ocrResolver(OCRProgress.allFields));
    const ocrCost = Number(finalProgress?.cost ?? 0);
    if (onCost && Number.isFinite(ocrCost) && ocrCost > 0) {
      onCost(ocrCost);
    }
    const rawAnswer = typeof finalProgress === 'string' ? finalProgress : finalProgress?.answer;
    const normalized = normalizeOCRResult(rawAnswer);
    return normalized;
  } catch (err) {
    logger.error(err, `OCR failed for image ${imageId}`);
    return { answer: '', confidence: 0, contentType: LEGACY_CONTENT_TYPE };
  }
}

/**
 * Create a BoundingBox with OCR-extracted text
 * @param region Region object with bbox {x1, y1, x2, y2} (normalized 0-1000) and pageNumber
 * @param imageArray Array of image IDs (indexed by page number)
 * @returns BoundingBox with ocrExtractedText populated and imageId pointing to cropped image
 */
export async function createBoundingBoxWithOCR(
  region: OCRRegionInput,
  imageArray: string[],
  extractionType: OcrExtractionType,
  taskType: string,
  onCost?: (cost: number) => void,
): Promise<BoundingBox> {
  const boundingBoxId = region.boundingBoxId ?? crypto.randomUUID();
  const pageIdx = (region.pageNumber || 1) - 1;

  if (pageIdx >= imageArray.length) {
    return new BoundingBox({
      _id: boundingBoxId,
      topLeft: new Point({ x: region.bbox.x1, y: region.bbox.y1 }),
      bottomRight: new Point({ x: region.bbox.x2, y: region.bbox.y2 }),
      pageNumber: region.pageNumber,
      imageId: null,
      ocrExtractedText: '',
      isHandwritten: false,
      contentType: LEGACY_CONTENT_TYPE,
    });
  }

  const sourceImageId = imageArray[pageIdx];
  if (!sourceImageId) {
    return new BoundingBox({
      _id: boundingBoxId,
      topLeft: new Point({ x: region.bbox.x1, y: region.bbox.y1 }),
      bottomRight: new Point({ x: region.bbox.x2, y: region.bbox.y2 }),
      pageNumber: region.pageNumber,
      imageId: null,
      ocrExtractedText: '',
      isHandwritten: false,
      contentType: LEGACY_CONTENT_TYPE,
    });
  }

  // Crop and save the region as a new ImageFile
  const croppedImage = await cropAndSaveRegion(sourceImageId, region.bbox);

  // For student answers of task types with forceImage, skip OCR entirely — the cropped image
  // region is passed directly to downstream LLM vision processing without text transcription.
  const behaviour = taskTypeBehaviours[taskType as TaskType];
  if (extractionType === OcrExtractionType.STUDENT_ANSWER && behaviour?.forceImage) {
    return new BoundingBox({
      _id: boundingBoxId,
      topLeft: new Point({ x: region.bbox.x1, y: region.bbox.y1 }),
      bottomRight: new Point({ x: region.bbox.x2, y: region.bbox.y2 }),
      pageNumber: region.pageNumber,
      imageId: croppedImage._id,
      ocrExtractedText: '',
      confidence: calculateBaseExtractionConfidence(region.confidence, 1),
      isHandwritten: false,
      contentType: 'image',
    });
  }

  // Run OCR on the cropped image (using full image since it's already cropped)
  const ocrResult = await runOCROnImage(croppedImage._id, extractionType, taskType || TaskType.DEFAULT, onCost);
  // An "image" region has no transcribable text by design. The text-based confidence
  // (calculateTaskExtractionConfidence) would treat the empty text as an unreadable artifact and
  // collapse confidence to 0, so for image content we base confidence on the bbx × OCR
  // classification confidence instead.
  const confidence =
    ocrResult.contentType === 'image'
      ? calculateBaseExtractionConfidence(region.confidence, ocrResult.confidence)
      : (
          await calculateTaskExtractionConfidence({
            bbxConfidence: region.confidence,
            ocrConfidence: ocrResult.confidence,
            extractedText: ocrResult.answer,
          })
        ).finalConfidence;

  return new BoundingBox({
    _id: boundingBoxId,
    topLeft: new Point({ x: region.bbox.x1, y: region.bbox.y1 }),
    bottomRight: new Point({ x: region.bbox.x2, y: region.bbox.y2 }),
    pageNumber: region.pageNumber,
    imageId: croppedImage._id, // Store the cropped image ID
    // An 'image' region has no transcribable text by design. We prohibit text output for it, but guard
    // here too so a box classified 'image' never carries stray text the model may have emitted anyway.
    ocrExtractedText: ocrResult.contentType === 'image' ? '' : ocrResult.answer,
    confidence,
    isHandwritten: typeof ocrResult.isHandwritten === 'boolean' ? ocrResult.isHandwritten : false,
    contentType: ocrResult.contentType,
  });
}

/**
 * Create multiple BoundingBoxes from regions
 * @param regions Array of region objects
 * @param imageArray Array of image IDs
 * @returns Array of BoundingBox objects with OCR text
 */
export async function createBoundingBoxesWithOCR(
  regions: OCRRegionInput[],
  imageArray: string[],
  extractionType: OcrExtractionType,
  onCost?: (cost: number) => void,
): Promise<BoundingBox[]> {
  const boundingBoxes: BoundingBox[] = [];
  for (const region of regions) {
    const bbox = await createBoundingBoxWithOCR(
      region,
      imageArray,
      extractionType,
      region.taskType || TaskType.DEFAULT,
      onCost,
    );
    boundingBoxes.push(bbox);
  }
  return boundingBoxes;
}
